import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [searchParams] = useSearchParams();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const practiceArea = searchParams.get('area') || 'corporate-law';

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const practiceAreas = {
    'corporate-law': {
      en: {
        title: 'Corporate Law',
        description: 'Comprehensive legal solutions for businesses of all sizes, from startups to established corporations. We provide strategic counsel on corporate governance, compliance, mergers and acquisitions, and business structuring.',
        icon: 'Building2'
      },
      sw: {
        title: 'Sheria ya Makampuni',
        description: 'Suluhisho kamili za kisheria kwa biashara za ukubwa wote, kutoka biashara mpya hadi makampuni makubwa. Tunatoa ushauri wa kimkakati kuhusu utawala wa makampuni, kufuata sheria, kuunganisha na ununuzi, na muundo wa biashara.',
        icon: 'Building2'
      }
    },
    'family-law': {
      en: {
        title: 'Family Law',
        description: 'Compassionate and professional legal representation for family matters including divorce, child custody, adoption, and matrimonial property disputes. We understand the sensitive nature of family issues.',
        icon: 'Heart'
      },
      sw: {
        title: 'Sheria ya Familia',
        description: 'Uwakilishi wa kisheria wa huruma na utaalamu kwa mambo ya familia ikiwa ni pamoja na talaka, ulinzi wa watoto, kupokea watoto, na migogoro ya mali za ndoa. Tunaelewa hali nyeti za masuala ya familia.',
        icon: 'Heart'
      }
    },
    'property-law': {
      en: {
        title: 'Property Law',
        description: 'Expert guidance on real estate transactions, property disputes, land registration, and conveyancing. We ensure your property interests are protected with thorough legal documentation.',
        icon: 'Home'
      },
      sw: {
        title: 'Sheria ya Mali',
        description: 'Uongozi wa kitaalamu katika miamala ya mali, migogoro ya mali, usajili wa ardhi, na uhamisho wa mali. Tunahakikisha maslahi yako ya mali yanalindwa kwa nyaraka za kisheria za kina.',
        icon: 'Home'
      }
    },
    'criminal-defense': {
      en: {
        title: 'Criminal Defense',
        description: 'Aggressive and strategic defense representation for criminal charges. We protect your rights and fight for the best possible outcome in criminal proceedings.',
        icon: 'Shield'
      },
      sw: {
        title: 'Utetezi wa Jinai',
        description: 'Uwakilishi wa utetezi mkali na wa kimkakati kwa mashtaka ya jinai. Tunalinda haki zako na kupigania matokeo bora zaidi katika michakato ya jinai.',
        icon: 'Shield'
      }
    }
  };

  const currentArea = practiceAreas[practiceArea] || practiceAreas['corporate-law'];
  const content = currentArea[currentLanguage];

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(currentLanguage === 'en' 
      ? `Hello, I would like to inquire about your ${content.title} services.`
      : `Hujambo, ningependa kuuliza kuhusu huduma zenu za ${content.title}.`);
    window.open(`https://wa.me/254721320787?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.open('tel:+254721320787', '_self');
  };

  return (
    <section className="relative bg-gradient-to-br from-surface/20 to-primary/10 py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl">
                <Icon name={content.icon} size={32} color="white" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-4xl lg:text-5xl font-heading font-bold text-text-primary">
                  {content.title}
                </h1>
                <p className="text-text-secondary font-caption mt-1">
                  {currentLanguage === 'en' ? 'Expert Legal Services' : 'Huduma za Kisheria za Kitaalamu'}
                </p>
              </div>
            </div>

            <p className="text-lg text-text-secondary leading-relaxed">
              {content.description}
            </p>

            {/* Contact Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                onClick={handleWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                size="lg"
                className="flex-1 sm:flex-none"
              >
                {currentLanguage === 'en' ? 'WhatsApp Consultation' : 'Mazungumzo ya WhatsApp'}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleCallClick}
                iconName="Phone"
                iconPosition="left"
                size="lg"
                className="flex-1 sm:flex-none"
              >
                {currentLanguage === 'en' ? 'Call Now' : 'Piga Simu Sasa'}
              </Button>
            </div>

            {/* Contact Info */}
            <div className="flex items-center space-x-6 text-sm text-text-secondary">
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={16} />
                <span>+254 721 320 787</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={16} />
                <span>info@peterpartnerslaw.co.ke</span>
              </div>
            </div>
          </div>

          {/* Visual Element */}
          <div className="relative">
            <div className="aspect-square bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl p-8 flex items-center justify-center">
              <div className="w-full h-full bg-white rounded-2xl shadow-lg flex items-center justify-center">
                <Icon name={content.icon} size={120} color="var(--color-primary)" strokeWidth={1.5} />
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent rounded-2xl flex items-center justify-center shadow-lg">
              <Icon name="Scale" size={32} color="white" strokeWidth={2} />
            </div>
            
            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-secondary rounded-2xl flex items-center justify-center shadow-lg">
              <Icon name="CheckCircle" size={24} color="var(--color-text-primary)" strokeWidth={2} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;